import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import API, { formatApiError } from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
    ArrowLeft, Video, Send, Sparkles, FileText, Users,
    MessageSquare, CheckCircle, Mic, MicOff, VideoOff
} from 'lucide-react';

export default function NegotiationRoomPage() {
    const { dealId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [deal, setDeal] = useState(null);
    const [room, setRoom] = useState(null);
    const [notes, setNotes] = useState([]);
    const [newNote, setNewNote] = useState('');
    const [loading, setLoading] = useState(true);
    const [jitsiLoaded, setJitsiLoaded] = useState(false);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [error, setError] = useState('');
    const jitsiContainerRef = useRef(null);
    const jitsiApiRef = useRef(null);

    // Load Jitsi external API
    useEffect(() => {
        if (document.querySelector('script[src*="meet.jit.si"]')) {
            setJitsiLoaded(true);
            return;
        }
        const script = document.createElement('script');
        script.src = 'https://meet.jit.si/external_api.js';
        script.async = true;
        script.onload = () => setJitsiLoaded(true);
        document.head.appendChild(script);
    }, []);

    const loadData = useCallback(async () => {
        try {
            const [dealRes, notesRes] = await Promise.all([
                API.get(`/deals/${dealId}`),
                API.get(`/negotiations/notes/${dealId}`).catch(() => ({ data: [] })),
            ]);
            setDeal(dealRes.data);
            setNotes(notesRes.data);
            // Create or get room
            const roomRes = await API.post('/negotiations/rooms', { deal_id: dealId });
            setRoom(roomRes.data);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, [dealId]);

    useEffect(() => { loadData(); }, [loadData]);

    // Initialize Jitsi when ready
    useEffect(() => {
        if (!jitsiLoaded || !room || !jitsiContainerRef.current || jitsiApiRef.current) return;
        try {
            const api = new window.JitsiMeetExternalAPI("meet.jit.si", {
                roomName: room.jitsi_room_id,
                parentNode: jitsiContainerRef.current,
                width: '100%',
                height: '100%',
                configOverwrite: {
                    startWithAudioMuted: true,
                    startWithVideoMuted: false,
                    prejoinPageEnabled: false,
                    disableDeepLinking: true,
                    toolbarButtons: [
                        'microphone', 'camera', 'desktop', 'fullscreen',
                        'chat', 'raisehand', 'tileview', 'hangup'
                    ],
                },
                interfaceConfigOverwrite: {
                    SHOW_JITSI_WATERMARK: false,
                    SHOW_WATERMARK_FOR_GUESTS: false,
                    TOOLBAR_ALWAYS_VISIBLE: true,
                    DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
                },
                userInfo: {
                    displayName: user?.name || 'Participant',
                    email: user?.email || '',
                },
            });
            jitsiApiRef.current = api;
        } catch (e) {
            console.error('Jitsi init error:', e);
        }
        return () => {
            if (jitsiApiRef.current) {
                jitsiApiRef.current.dispose();
                jitsiApiRef.current = null;
            }
        };
    }, [jitsiLoaded, room, user]);

    const addNote = async () => {
        if (!newNote.trim()) return;
        try {
            await API.post('/negotiations/notes', { deal_id: dealId, content: newNote });
            setNewNote('');
            const { data } = await API.get(`/negotiations/notes/${dealId}`);
            setNotes(data);
        } catch (err) { console.error(err); }
    };

    const generateSummary = async () => {
        setSummaryLoading(true);
        try {
            await API.post('/negotiations/ai-summary', { deal_id: dealId });
            const { data } = await API.get(`/negotiations/notes/${dealId}`);
            setNotes(data);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail));
        } finally {
            setSummaryLoading(false);
        }
    };

    const endSession = async () => {
        try {
            await API.put(`/negotiations/rooms/${dealId}/complete`);
            if (jitsiApiRef.current) {
                jitsiApiRef.current.executeCommand('hangup');
            }
            navigate(`/deals/${dealId}`);
        } catch (err) { console.error(err); }
    };

    if (loading) return (
        <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    return (
        <div className="min-h-screen bg-[#0A0A0A]" data-testid="negotiation-room-page">
            {/* Header Bar */}
            <div className="h-14 bg-[#171717] border-b border-white/10 flex items-center justify-between px-4">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" onClick={() => navigate(`/deals/${dealId}`)} className="text-zinc-400 hover:text-white h-8 px-2" data-testid="neg-back-btn">
                        <ArrowLeft className="w-4 h-4" />
                    </Button>
                    <div className="flex items-center gap-2">
                        <Video className="w-4 h-4 text-emerald-500" />
                        <span className="text-sm font-medium text-white font-['Outfit']">Negotiation Room</span>
                    </div>
                    {deal && (
                        <span className="text-xs text-zinc-400 hidden sm:inline">| {deal.property_address}</span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <Badge className="bg-emerald-900/50 text-emerald-400 border border-emerald-800">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5 animate-pulse" />
                        Live
                    </Badge>
                    <Button size="sm" variant="outline" onClick={endSession} className="border-red-800 text-red-400 hover:bg-red-900/50 h-8 text-xs" data-testid="end-session-btn">
                        End Session
                    </Button>
                </div>
            </div>

            {error && <div className="mx-4 mt-2 p-2 rounded bg-red-900/50 text-red-300 text-xs">{error}</div>}

            {/* Main Layout */}
            <div className="flex h-[calc(100vh-56px)]">
                {/* Video Area */}
                <div className="flex-1 relative">
                    <div ref={jitsiContainerRef} className="w-full h-full bg-[#0A0A0A]" data-testid="jitsi-container">
                        {!jitsiLoaded && (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-center">
                                    <Video className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                                    <p className="text-sm text-zinc-400">Loading video conference...</p>
                                </div>
                            </div>
                        )}
                    </div>
                    {/* Encryption Badge */}
                    <div className="absolute bottom-4 left-4 flex items-center gap-1.5 bg-black/70 backdrop-blur-sm rounded-full px-3 py-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        <span className="text-xs text-zinc-300">End-to-end encrypted (SRTP)</span>
                    </div>
                </div>

                {/* AI Secretary Panel */}
                <div className="w-[380px] bg-[#171717] border-l border-white/10 flex flex-col" data-testid="ai-secretary-panel">
                    <div className="p-4 border-b border-white/10">
                        <div className="flex items-center gap-2 mb-1">
                            <Sparkles className="w-4 h-4 text-amber-400" />
                            <span className="text-sm font-medium text-white font-['Outfit']">AI Secretary</span>
                        </div>
                        <p className="text-xs text-zinc-400">Takes notes, drafts summaries, suggests deal terms</p>
                    </div>

                    {/* Notes List */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="meeting-notes-list">
                        {notes.length === 0 ? (
                            <div className="text-center py-12">
                                <MessageSquare className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                                <p className="text-xs text-zinc-500">No notes yet. Add notes during the meeting.</p>
                            </div>
                        ) : notes.map(n => (
                            <div key={n.id} className={`rounded-lg p-3 ${n.note_type === 'ai_summary' ? 'bg-amber-900/20 border border-amber-800/30' : 'bg-white/5 border border-white/5'}`}>
                                <div className="flex items-center justify-between mb-1.5">
                                    <div className="flex items-center gap-1.5">
                                        {n.note_type === 'ai_summary' ? (
                                            <Sparkles className="w-3 h-3 text-amber-400" />
                                        ) : (
                                            <Users className="w-3 h-3 text-zinc-400" />
                                        )}
                                        <span className="text-xs font-medium text-zinc-300">{n.created_by_name}</span>
                                    </div>
                                    <span className="text-xs text-zinc-500">{new Date(n.created_at).toLocaleTimeString()}</span>
                                </div>
                                <p className="text-xs text-zinc-300 leading-relaxed">{n.content}</p>
                                {n.note_type === 'ai_summary' && n.metadata && (
                                    <div className="mt-2 space-y-2">
                                        {n.metadata.key_points?.length > 0 && (
                                            <div>
                                                <div className="text-xs font-medium text-amber-400 mb-1">Key Points</div>
                                                {n.metadata.key_points.map((p, i) => (
                                                    <div key={i} className="text-xs text-zinc-400 flex items-start gap-1">
                                                        <CheckCircle className="w-2.5 h-2.5 text-emerald-500 mt-0.5 shrink-0" /> {p}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {n.metadata.action_items?.length > 0 && (
                                            <div>
                                                <div className="text-xs font-medium text-amber-400 mb-1">Action Items</div>
                                                {n.metadata.action_items.map((a, i) => (
                                                    <div key={i} className="text-xs text-zinc-400">
                                                        <span className="text-zinc-300">{a.assigned_to}:</span> {a.item}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {n.metadata.suggested_terms?.notes && (
                                            <div className="bg-emerald-900/30 rounded p-2 border border-emerald-800/30">
                                                <div className="text-xs font-medium text-emerald-400 mb-1">Suggested Terms</div>
                                                <p className="text-xs text-emerald-300">{n.metadata.suggested_terms.notes}</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Actions */}
                    <div className="p-4 border-t border-white/10 space-y-2">
                        <Button onClick={generateSummary} disabled={summaryLoading || notes.filter(n => n.note_type === 'manual').length === 0}
                            className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs h-8" data-testid="generate-summary-btn">
                            <Sparkles className="w-3 h-3 mr-1.5" />
                            {summaryLoading ? 'Generating Summary...' : 'AI Summary & Terms'}
                        </Button>
                        <div className="flex gap-2">
                            <Textarea value={newNote} onChange={e => setNewNote(e.target.value)}
                                placeholder="Add meeting note..."
                                className="bg-white/5 border-white/10 text-white text-xs min-h-10 h-10 resize-none placeholder:text-zinc-500"
                                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addNote(); } }}
                                data-testid="meeting-note-input" />
                            <Button onClick={addNote} className="bg-emerald-700 hover:bg-emerald-600 rounded-lg px-3 h-10" data-testid="add-note-btn">
                                <Send className="w-3.5 h-3.5" />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
